import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { BookOpen, Globe, Trash2, MessageSquare, Hash, Send } from 'lucide-react';
import { showToast } from '../utils/toast';
import { readJsonErrorResponse } from '../utils/portalHelpers';

const WEBHOOK_CHANNEL_TYPES = [
  { id: 'discord', label: 'Discord', icon: MessageSquare, color: 'text-indigo-500' },
  { id: 'slack', label: 'Slack', icon: Hash, color: 'text-amber-500' },
  { id: 'generic', label: 'Webhook Genérico', icon: Globe, color: 'text-emerald-500' }
];

const WEBHOOK_ALERT_PORTAL_IDS = ['nfe', 'nfce', 'mdfe', 'cte', 'cte_svrs', 'nfe_svrs'];
const WEBHOOK_ALERT_TYPE_ORDER = ['service_availability', 'new_document'];
const WEBHOOK_ALERT_TYPE_META = {
  service_availability: { label: 'Disponibilidade' },
  new_document: { label: 'Novos Documentos' }
};

const WEBHOOK_ALERT_PORTAL_LABELS = {
  nfe: 'NF-e SEFAZ',
  nfce: 'NFC-e SVRS',
  mdfe: 'MDF-e SVRS',
  cte: 'CT-e SEFAZ',
  cte_svrs: 'CT-e SVRS',
  nfe_svrs: 'NF-e SVRS'
};

export default function WebhookAlertsPanel({
  apiToken,
  buildRequestHeaders,
  getApiUrl,
  isAdminAuthenticated,
  portals
}) {
  const [webhooks, setWebhooks] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(null);
  const [isTesting, setIsTesting] = useState(null);

  // Form states for new webhook
  const [newWebhookUrl, setNewWebhookUrl] = useState('');
  const [newWebhookName, setNewWebhookName] = useState('');
  const [newWebhookType, setNewWebhookType] = useState('discord');
  const [selectedTopics, setSelectedTopics] = useState([]);
  const canManageWebhooks = isAdminAuthenticated || Boolean(String(apiToken || '').trim());

  const allPossibleTopics = useMemo(() => {
    return WEBHOOK_ALERT_TYPE_ORDER.flatMap(type => 
      WEBHOOK_ALERT_PORTAL_IDS.map(portalId => ({
        id: `${type}:${portalId}`,
        label: `${WEBHOOK_ALERT_TYPE_META[type].label} - ${WEBHOOK_ALERT_PORTAL_LABELS[portalId] || portalId}`
      }))
    );
  }, []);

  const loadWebhooks = useCallback(async () => {
    setIsLoading(true);
    try {
      const baseUrl = getApiUrl('fiscal-webhooks');
      const response = await fetch(baseUrl, {
        headers: buildRequestHeaders()
      });
      const payload = await readJsonErrorResponse(response);
      if (response.ok) {
        setWebhooks(payload.webhooks || []);
      }
    } catch (error) {
      showToast('Falha ao carregar webhooks.', { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [buildRequestHeaders, getApiUrl]);

  useEffect(() => {
    if (canManageWebhooks) {
      loadWebhooks();
    }
  }, [loadWebhooks, canManageWebhooks]);

  const handleCreateWebhook = async (e) => {
    e.preventDefault();
    if (!newWebhookUrl.trim()) {
      showToast('Informe a URL do webhook.', { type: 'warning' });
      return;
    }
    if (selectedTopics.length === 0) {
      showToast('Selecione pelo menos um tópico.', { type: 'warning' });
      return;
    }

    setIsSaving(true);
    try {
      const response = await fetch(getApiUrl('fiscal-webhooks'), {
        method: 'POST',
        headers: buildRequestHeaders({ contentType: 'application/json' }),
        body: JSON.stringify({
          url: newWebhookUrl.trim(),
          name: newWebhookName.trim() || 'Meu Webhook',
          channelType: newWebhookType,
          topics: selectedTopics
        })
      });

      if (response.ok) {
        showToast('Webhook salvo com sucesso!', { type: 'success' });
        setNewWebhookUrl('');
        setNewWebhookName('');
        setSelectedTopics([]);
        loadWebhooks();
      } else {
        const data = await response.json();
        const errorMessage = data.error || 'Falha ao salvar.';
        throw new Error(errorMessage);
      }
    } catch (error) {
      showToast(error.message, { type: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteWebhook = async (id) => {
    if (!window.confirm('Tem certeza que deseja remover este webhook?')) return;
    setIsDeleting(id);
    try {
      const response = await fetch(`${getApiUrl('fiscal-webhooks')}?id=${id}`, {
        method: 'DELETE',
        headers: buildRequestHeaders()
      });
      if (response.ok) {
        showToast('Webhook removido.', { type: 'success' });
        loadWebhooks();
      }
    } catch (error) {
      showToast('Erro ao remover.', { type: 'error' });
    } finally {
      setIsDeleting(null);
    }
  };

  const handleTestWebhook = async (id) => {
    setIsTesting(id);
    try {
      const response = await fetch(getApiUrl('fiscal-webhooks'), {
        method: 'POST',
        headers: {
          ...buildRequestHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          action: 'test',
          testId: id 
        })
      });
      if (response.ok) {
        showToast('Notificação de teste enviada!', { type: 'success' });
      } else {
        const data = await response.json();
        const errorMessage = data.error || 'Falha ao disparar teste.';
        throw new Error(errorMessage);
      }
    } catch (error) {
      showToast(error.message, { type: 'error' });
    } finally {
      setIsTesting(null);
    }
  };

  const toggleTopic = (topicId) => {
    setSelectedTopics(prev => 
      prev.includes(topicId) ? prev.filter(t => t !== topicId) : [...prev, topicId]
    );
  };

  return (
    <div className="space-y-6">
      {/* Formulário de Novo Webhook */}
      <form onSubmit={handleCreateWebhook} className="rounded-2xl border border-indigo-100 dark:border-indigo-900/40 bg-white/80 dark:bg-gray-900/40 p-5 shadow-sm space-y-4">
        <div>
          <p className="text-[10px] font-black uppercase tracking-widest text-indigo-600 dark:text-indigo-400 mb-1">Novo Canal de Notificação</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">Configure um webhook para receber alertas em tempo real.</p>
          <a
            href="/configurar-slack-webhook"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-widest text-indigo-600 hover:text-indigo-700 hover:underline dark:text-indigo-300 dark:hover:text-indigo-200"
          >
            <BookOpen size={13} />
            Abrir documentação separada
          </a>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <input
            type="url"
            value={newWebhookUrl}
            onChange={(e) => setNewWebhookUrl(e.target.value)}
            placeholder="URL do Webhook (Discord, Slack, etc)"
            required
            className="w-full h-11 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-2xl px-4 text-sm focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
          />
          <input
            type="text"
            value={newWebhookName}
            onChange={(e) => setNewWebhookName(e.target.value)}
            placeholder="Nome Amigável"
            className="w-full h-11 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-2xl px-4 text-sm focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
          />
        </div>

        <div className="flex flex-wrap gap-2">
          {WEBHOOK_CHANNEL_TYPES.map(type => (
            <button
              key={type.id}
              type="button"
              onClick={() => setNewWebhookType(type.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl border text-xs font-bold transition-all ${
                newWebhookType === type.id 
                  ? 'border-indigo-500 bg-indigo-500 text-white' 
                  : 'border-gray-200 bg-white dark:bg-gray-800 dark:border-gray-700 text-gray-600 dark:text-gray-300'
              }`}
            >
              <type.icon size={14} className={newWebhookType === type.id ? 'text-white' : type.color} />
              {type.label}
            </button>
          ))}
        </div>

        <div className="space-y-2">
          <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">Tópicos de Interesse</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {allPossibleTopics.map(topic => (
              <button
                key={topic.id}
                type="button"
                onClick={() => toggleTopic(topic.id)}
                className={`text-left p-2 rounded-lg border text-[10px] font-medium transition-all ${
                  selectedTopics.includes(topic.id)
                    ? 'border-indigo-300 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300'
                    : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-500'
                }`}
              >
                {topic.label}
              </button>
            ))}
          </div>
        </div>

        <button
          type="submit"
          disabled={isSaving}
          className="w-full h-11 rounded-2xl bg-indigo-600 text-white text-[10px] font-black uppercase tracking-widest shadow-lg shadow-indigo-600/20 hover:bg-indigo-700 transition-colors disabled:opacity-50"
        >
          {isSaving ? 'Salvando...' : 'Adicionar Webhook'}
        </button>
      </form>

      {/* Lista de Webhooks Ativos */}
      {canManageWebhooks && (
        <div className="space-y-3 pt-6 border-t border-gray-100 dark:border-gray-800">
          <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 px-2">Webhooks Configurados</p>
          {isLoading ? (
            <div className="p-8 text-center animate-pulse text-gray-400">Carregando...</div>
          ) : webhooks.length === 0 ? (
            <div className="p-8 text-center rounded-2xl border-2 border-dashed border-gray-200 dark:border-gray-800 text-gray-400 text-sm">
              Nenhum webhook configurado.
            </div>
          ) : (
            <div className="grid gap-3">
              {webhooks.map(webhook => {
                const typeInfo = WEBHOOK_CHANNEL_TYPES.find(t => t.id === webhook.channel_type) || WEBHOOK_CHANNEL_TYPES[2];
                return (
                  <div key={webhook.id} className="flex items-center justify-between p-4 rounded-2xl border border-gray-200 dark:border-gray-700 bg-white/50 dark:bg-gray-800/50 gap-3 min-w-0">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className={`p-2 rounded-lg bg-gray-100 dark:bg-gray-900 shrink-0 ${typeInfo.color}`}>
                        <typeInfo.icon size={18} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-bold text-gray-700 dark:text-gray-200 truncate" title={webhook.name}>{webhook.name}</p>
                        <p className="text-[10px] text-gray-400 truncate" title={webhook.url}>{webhook.url}</p>
                        <div className="flex flex-wrap gap-1 mt-1 overflow-hidden">
                          {webhook.topics?.map(t => (
                            <span key={t} className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-900 text-[8px] text-gray-500 uppercase font-bold whitespace-nowrap">
                              {t.replace(':', ' - ')}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => handleTestWebhook(webhook.id)}
                        disabled={isTesting === webhook.id}
                        className="p-2 text-gray-400 hover:text-indigo-500 transition-colors disabled:opacity-50"
                        title="Testar Webhook"
                      >
                        <Send size={16} className={isTesting === webhook.id ? 'animate-pulse' : ''} />
                      </button>
                      <button
                        onClick={() => handleDeleteWebhook(webhook.id)}
                        disabled={isDeleting === webhook.id}
                        className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                        title="Excluir Webhook"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
      {!canManageWebhooks && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-xs leading-6 text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/20 dark:text-amber-200">
          Informe uma API Key com escopo <code className="rounded bg-amber-100 px-1 py-0.5 font-mono text-[11px] dark:bg-amber-900/40">notifications</code> para listar, testar ou remover webhooks. Administradores também podem gerenciar por token admin.
        </div>
      )}
    </div>
  );
}
